import React, { Fragment } from "react";
import { createPortal } from "react-dom";
import "./Modal.scss";
import { FaTimes } from "react-icons/fa";
import {
  FEEDBACK,
  DELETE_ACCOUNT,
  EDIT_COMPONENT,
  PREMIUM_ONLY,
  PREMIM_ONLY_SAVE,
  CANCEL_PREMIUM
} from "./modalTypes";
import FeedbackContainer from "../feedback/FeedbackContainer";
import DeleteAccountContainer from "../deleteAccount/DeleteAccountContainer";
import { handleEnterKeypress } from "../../utils/keyEvents";
import { useMobile } from "../../custom-hooks/useMobile";
import classNames from "classnames";
import EditWidgetModal from "../widget/EditWidgetModal";
import {
  TICKER_BANNER,
  BOOKMARK_LIST,
  BOOKMARK,
  MARKET_OVERVIEW,
  CLOCK,
  ICON_BOOKMARK,
  RICH_TEXT
} from "../widget/widgetUtils";
import TitleAndLogo from "../titleAndLogo/TitleAndLogo";
import PremiumOnlyModal from "./PremiumOnlyModal";
import PremiumOnlySaveModal from "./PremiumOnlySaveModal";
import CancelPremiumModal from "./CancelPremiumModal";
import type { Widget } from "../../types/models";

export interface ModalProps {
  /** Names the feature that triggered a premium-only modal. */
  subType?: string;
  editWidgetIdentifiers?: string[];
  widgetList?: Widget[];
  closeModal?: () => void;
  isModalVisible?: boolean;
  modalType?: string;
}

export default function Modal(props: ModalProps) {
  const { isMobile } = useMobile({});

  const selectCorrectForm = (type: string | undefined) => {
    switch (type) {
      case FEEDBACK:
        return <FeedbackContainer />;
      case DELETE_ACCOUNT:
        return <DeleteAccountContainer />;
      case EDIT_COMPONENT:
        return <EditWidgetModal />;
      case PREMIUM_ONLY:
        return <PremiumOnlyModal subType={props.subType} />;
      case PREMIM_ONLY_SAVE:
        return <PremiumOnlySaveModal />;
      case CANCEL_PREMIUM:
        return <CancelPremiumModal />;
      default:
        return null;
    }
  };

  const selectEditWidgetTitle = () => {
    const { editWidgetIdentifiers = [], widgetList = [] } = props;
    const widget_id =
      editWidgetIdentifiers.length > 0 ? editWidgetIdentifiers[0] : null;
    const widget = widgetList.find(entry => entry.widget_id === widget_id);

    switch (widget?.widgetType) {
      case TICKER_BANNER:
        return "Edit Ticker Banner";
      case MARKET_OVERVIEW:
        return "Edit Market Overview";
      case BOOKMARK_LIST:
        return "Edit Bookmark List";
      case CLOCK:
        return "Edit Clock";
      case ICON_BOOKMARK:
        return "Edit Icon Bookmark";
      case BOOKMARK:
        return "Edit Bookmark";
      case RICH_TEXT:
        return "Add Custom Text";
      default:
        return "No Component Found";
    }
  };

  const selectFormTitle = (type: string | undefined) => {
    switch (type) {
      case FEEDBACK:
        return "Provide Feedback";
      case DELETE_ACCOUNT:
        return "Are you sure?";
      case EDIT_COMPONENT:
        return selectEditWidgetTitle();
      case PREMIUM_ONLY:
        return "This is a premium feature";
      case PREMIM_ONLY_SAVE:
        return "Upgrade your account";
      case CANCEL_PREMIUM:
        return "We're sad to see you go!";
      default:
        return "";
    }
  };

  const onCloseButtonKeyPress = (event: React.KeyboardEvent) =>
    handleEnterKeypress(event, () => props.closeModal?.());

  const constructChildren = () => {
    const { closeModal, modalType } = props;
    return (
      <Fragment>
        <div className="glass" onClick={closeModal} data-testid="glass" />
        <div className={classNames("modal", isMobile ? "mobile-view" : "")}>
          <div className="modal-header">
            <TitleAndLogo
              shouldShowLogo={false}
              title={selectFormTitle(modalType)}
            />
            <FaTimes
              onClick={closeModal}
              onKeyPress={onCloseButtonKeyPress}
              className="close-modal-icon"
              height={"14px"}
              width={"14px"}
              data-testid="close-modal-icon"
              tabIndex={0}
            />
          </div>
          <div>{selectCorrectForm(modalType)}</div>
        </div>
      </Fragment>
    );
  };

  return props.isModalVisible
    ? createPortal(constructChildren(), document.body)
    : null;
}
